/**
 * ADR-0185 — pure math behind the machine-scheduling view's faces: the
 * next-up derivation per machine, the setup-cost color scale, the time window
 * per zoom level, and the unstaffed-hours shading intervals from the calendar
 * windows. No DOM, no fetches — unit-tested in laneMath.test.ts.
 */
import type { BoardCalendarWindow, BoardLane, BoardLaneJob } from "../../boardTypes.js";

// ---------------------------------------------------------------------------
// Next-up rail derivation
// ---------------------------------------------------------------------------

export interface NextUpRow {
  machine: string;
  running: BoardLaneJob | null;
  next: BoardLaneJob | null;
  jobCount: number;
  atRiskCount: number;
}

/**
 * One rail line per machine: what runs now (the lane's running job — running
 * work leads its machine's sequence) and what comes next (the first job that
 * is not already running). The next job's own setup_class/setup_hours IS the
 * changeover into it.
 */
export function deriveNextUp(lanes: BoardLane[]): NextUpRow[] {
  return lanes.map((lane) => {
    const running = lane.jobs.find((j) => j.running) ?? null;
    const next = lane.jobs.find((j) => !j.running) ?? null;
    return {
      machine: lane.machine,
      running,
      next,
      jobCount: lane.jobs.length,
      atRiskCount: lane.jobs.filter((j) => j.at_risk).length,
    };
  });
}

/**
 * A lane segment's identity: the job number leads, the part follows in
 * parentheses when there is one. The visible label and the segment's
 * aria-label are both built from this, so what is seen and what is announced
 * cannot drift apart.
 */
export function jobLabel(j: Pick<BoardLaneJob, "job" | "part">): string {
  return j.part ? `${j.job} (${j.part})` : j.job;
}

/**
 * One labelled line in a hover card: a bold label and its value. Owner,
 * 2026-09-19, on the setup popups: "their popups need organized structure. As
 * it stands, there's no punctuation or capitalization...hard to read." So the
 * cards return LABELLED rows rather than bare sentence fragments, and every
 * value we author reads as a capitalised phrase.
 */
export type CardRow = { label: string; value: string; muted?: boolean };

export interface ChangeoverCard {
  /** "Changeover into {job number} ({part})" — the full-width header. */
  transition: string;
  /** The labelled body: class, setup hours, sample size, pricing basis. */
  rows: CardRow[];
}

/**
 * The hover card for a changeover symbol — the gap fill in a lane and the
 * class chip on the Next-Up rail show the same card. Callers gate on
 * setup_class being present (no class means no changeover to describe).
 * `measuredCounts` maps class label → measured changeover count (the board
 * read's changeover_classes); omit it and the sample row is omitted.
 *
 * The CLASS VALUE is served data and is printed exactly as served — we label
 * it rather than re-casing it, so what the card shows is what the cost table
 * holds.
 */
export function changeoverCard(
  j: Pick<BoardLaneJob, "job" | "part" | "setup_class" | "setup_hours" | "setup_cost_fallback">,
  measuredCounts?: Map<string, number | null>
): ChangeoverCard {
  const count = !j.setup_cost_fallback && j.setup_class ? measuredCounts?.get(j.setup_class) ?? null : null;
  const rows: CardRow[] = [];
  if (j.setup_class) rows.push({ label: "Class", value: j.setup_class });
  rows.push({ label: "Setup", value: `${j.setup_hours} h` });
  if (typeof count === "number" && count > 0) {
    rows.push({
      label: "Based on",
      value: `${count} measured changeover${count === 1 ? "" : "s"}`,
      muted: true,
    });
  }
  if (j.setup_cost_fallback) {
    rows.push({
      label: "Priced",
      value: "Estimated — no measured class, so the full changeover is charged.",
      muted: true,
    });
  }
  return { transition: `Changeover into ${jobLabel(j)}`, rows };
}

/** The class → measured-count lookup the hover cards read (Addendum 5). */
export function measuredCountsOf(
  classes: Array<{ label: string; measured_count: number | null }> | undefined
): Map<string, number | null> {
  return new Map((classes ?? []).map((c) => [c.label, c.measured_count]));
}

/** 1st / 2nd / 3rd / 4th … (English ordinal suffix, 11–13 special-cased). */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  const rem10 = n % 10;
  return `${n}${rem10 === 1 ? "st" : rem10 === 2 ? "nd" : rem10 === 3 ? "rd" : "th"}`;
}

/** True when the job sits at a different position than the Plex schedule puts it (the differs marker). */
export function movedFromCurrentPlan(j: Pick<BoardLaneJob, "proposed_rank" | "current_rank">): boolean {
  return j.proposed_rank != null && j.current_rank != null && j.proposed_rank !== j.current_rank;
}

/**
 * Whether a proposed segment wears the moved corner dot. Conductor-ruled
 * (2026-09-04): NEVER on a running job — the proposal holds running work at
 * the front by reality, not by choice, so a marker there says "the optimizer
 * moved this" about a job nobody can move. Its hover may still show both
 * ranks.
 */
export function movedMarkerVisible(
  j: Pick<BoardLaneJob, "proposed_rank" | "current_rank" | "running">
): boolean {
  return !j.running && movedFromCurrentPlan(j);
}

/**
 * The ruled hover sentence — "was 5th on this machine, now 2nd" — or null
 * when the job didn't move or either rank is unknown (no invented positions).
 * (Phase A retires the approximate-times hover caveat with the constructed
 * walk; since 2026-10-08 the Plex track is Plex's order laid out from now,
 * and its hover names Plex's booked start — overdue when it is earlier.)
 */
export function rankChangeLine(j: Pick<BoardLaneJob, "proposed_rank" | "current_rank">): string | null {
  if (!movedFromCurrentPlan(j)) return null;
  return `was ${ordinal(j.current_rank!)} on this machine, now ${ordinal(j.proposed_rank!)}`;
}

// ---------------------------------------------------------------------------
// Setup-class color scale (cheap → green, full change → red)
// ---------------------------------------------------------------------------

/** Max setup hours across a set of lanes — the scale's red end. */
export function maxSetupHours(lanes: BoardLane[]): number {
  let max = 0;
  for (const lane of lanes) {
    for (const j of lane.jobs) {
      if (j.setup_hours > max) max = j.setup_hours;
    }
  }
  return max;
}

/**
 * Interpolated hue for a changeover's measured cost: 0h → green (120°),
 * the plan's costliest → red (0°). Returns a CSS hsl() string.
 */
export function setupColor(setupHours: number, max: number): string {
  if (max <= 0) return "hsl(120, 45%, 45%)";
  const t = Math.min(1, Math.max(0, setupHours / max));
  const hue = Math.round(120 * (1 - t));
  return `hsl(${hue}, 55%, 45%)`;
}

// ---------------------------------------------------------------------------
// Zoom windows
// ---------------------------------------------------------------------------

export type ZoomLevel = "today" | "three_days" | "full";

export interface TimeWindow {
  startMs: number;
  endMs: number;
}

/**
 * The full extent of a set of lanes (min start → max end), or null when empty.
 *
 * `nowMs` clamps the LEFT EDGE to the present. A running job is drawn from its
 * ACTUAL start (iteration 2R — running work is a fact), and a job that has been
 * running since May would otherwise stretch the domain back to May and squeeze
 * every other bar into a sliver. Today is the earliest date on the grid, in
 * every zoom including Full; a bar that began earlier is clamped at the left
 * edge by positionIn and names its true start on hover.
 *
 * A plan lying WHOLLY in the past keeps its own span — an archived proposal
 * still renders rather than collapsing to an empty window.
 */
export function laneExtent(lanes: BoardLane[], nowMs?: number): TimeWindow | null {
  let min = Infinity;
  let max = -Infinity;
  for (const lane of lanes) {
    for (const j of lane.jobs) {
      const s = j.start ? Date.parse(j.start) : NaN;
      const e = j.end ? Date.parse(j.end) : NaN;
      if (!Number.isNaN(s) && s < min) min = s;
      if (!Number.isNaN(e) && e > max) max = e;
    }
  }
  if (!(Number.isFinite(min) && Number.isFinite(max) && max > min)) return null;
  if (nowMs != null && nowMs > min && nowMs < max) min = nowMs;
  return { startMs: min, endMs: max };
}

/**
 * The visible window for a zoom level. Today and 3-days anchor at the LATER of
 * the plan's start and one hour ago (an overnight plan opens on the working
 * present, an old plan on its own span); Full is the whole extent — whose left
 * edge is already Now whenever the plan straddles it (see laneExtent).
 */
export function zoomWindow(zoom: ZoomLevel, extent: TimeWindow, nowMs: number): TimeWindow {
  if (zoom === "full") return extent;
  const anchor = Math.max(extent.startMs, Math.min(nowMs - 3_600_000, extent.endMs - 3_600_000));
  const span = zoom === "today" ? 24 * 3_600_000 : 72 * 3_600_000;
  return { startMs: anchor, endMs: Math.min(anchor + span, extent.endMs) };
}

/**
 * Sub-row layout for the PLEX line: which stacked row each bar belongs to, and
 * how many rows the line needs.
 *
 * Used on the Plex line ONLY. Iteration 3 item A draws the dispatcher's own
 * booked times, and Plex routinely books two jobs over each other on a machine
 * — an overlap there is Plex's fact and is itself the finding, so both bars
 * have to stay readable. The Optimized and Refined lines are OUR plan, where
 * one machine runs one job at a time and concurrency is a defect the hub
 * removes rather than a thing to draw (owner's ruling, 2026-09-18).
 *
 * Greedy interval partition: walking bars in start order, each takes the first
 * row whose last bar has already ended, and a freed row is reused. A machine
 * Plex has booked cleanly lands entirely in row 0 and looks exactly as it did.
 * The returned `row` is parallel to the INPUT array, not to start order.
 */
/**
 * Whether a job's bar lands anywhere in the window. This is the ONE predicate
 * the renderer, the stacker and Find share: a bar that is not drawn must not
 * claim a row, and a bar that is drawn must be found. The window's left edge
 * is Now, so everything the dispatcher booked in the past fails it.
 */
export function drawsInWindow(j: Pick<BoardLaneJob, "start" | "end">, window: TimeWindow): boolean {
  const s = j.start ? Date.parse(j.start) : NaN;
  const e = j.end ? Date.parse(j.end) : NaN;
  return !Number.isNaN(s) && !Number.isNaN(e) && e >= window.startMs && s <= window.endMs;
}

/**
 * Sub-row per job for the Plex line. Only jobs that will draw in `window`
 * take part: a past-booked job overlaps the long-running leader just as much
 * as a future one, but it is never drawn, so counting it sized rows for bars
 * nobody could see (ten of eighteen prod machines, 2026-09-18). Two drawable
 * bars that overlap at all overlap inside the window, so no clamping is
 * needed — filtering alone gives the stacking the eye sees.
 */
export function stackRows(jobs: BoardLaneJob[], window?: TimeWindow): { row: number[]; rows: number } {
  const row = new Array<number>(jobs.length).fill(0);
  const order = jobs
    .map((j, i) => ({ i, s: j.start ? Date.parse(j.start) : NaN, e: j.end ? Date.parse(j.end) : NaN }))
    .filter((x) => !Number.isNaN(x.s) && !Number.isNaN(x.e))
    .filter((x) => !window || (x.e >= window.startMs && x.s <= window.endMs))
    .sort((a, b) => a.s - b.s || a.e - b.e);
  const lastEnd: number[] = [];
  for (const x of order) {
    let r = lastEnd.findIndex((end) => end <= x.s);
    if (r === -1) { r = lastEnd.length; lastEnd.push(x.e); } else { lastEnd[r] = x.e; }
    row[x.i] = r;
  }
  return { row, rows: Math.max(1, lastEnd.length) };
}

/** Fraction [0,1] of an instant inside a window (clamped). */
export function positionIn(window: TimeWindow, ms: number): number {
  const span = window.endMs - window.startMs;
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (ms - window.startMs) / span));
}

// ---------------------------------------------------------------------------
// Time-axis ticks (Addendum 3 — the labeled axis over the lane stack)
// ---------------------------------------------------------------------------

export interface AxisTick {
  ms: number;
  /** Fraction of the visible window. */
  frac: number;
  /** "day" = dated boundary, "hour" = labeled hour, "grid" = unlabeled line. */
  kind: "day" | "hour" | "grid";
  label: string | null;
}

function hourLabel(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: "numeric" });
}

function dayLabel(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/**
 * Ticks for the visible window, all strictly inside it (the window-range line
 * already names the edges). Local wall-clock alignment throughout.
 *
 * - today: hour labels at the first whole hour then every 4 h; a dated label
 *   where a midnight falls (the date wins over a coinciding hour tick).
 * - three_days: dated labels at each midnight, unlabeled gridlines every 6 h
 *   on the 0/6/12/18 clock.
 * - full: dated midnights only, thinning to every 2nd/3rd… day when the
 *   window carries more than ~8 boundaries.
 */
export function timeTicks(window: TimeWindow, zoom: ZoomLevel): AxisTick[] {
  const ticks: AxisTick[] = [];
  const push = (ms: number, kind: AxisTick["kind"], label: string | null) => {
    if (ms > window.startMs && ms < window.endMs) {
      ticks.push({ ms, frac: positionIn(window, ms), kind, label });
    }
  };

  // Local midnights inside the window (day arithmetic, so DST never drifts).
  const midnights: number[] = [];
  const d = new Date(window.startMs);
  d.setHours(0, 0, 0, 0);
  if (d.getTime() <= window.startMs) d.setDate(d.getDate() + 1);
  while (d.getTime() < window.endMs) {
    midnights.push(d.getTime());
    d.setDate(d.getDate() + 1);
  }

  if (zoom === "full") {
    const step = Math.max(1, Math.ceil(midnights.length / 8));
    midnights.forEach((ms, i) => {
      if (i % step === 0) push(ms, "day", dayLabel(ms));
    });
    return ticks;
  }

  for (const ms of midnights) push(ms, "day", dayLabel(ms));

  const h = new Date(window.startMs);
  h.setMinutes(0, 0, 0);
  while (h.getTime() <= window.startMs) h.setHours(h.getHours() + 1);

  if (zoom === "today") {
    for (let t = h.getTime(); t < window.endMs; h.setHours(h.getHours() + 4), t = h.getTime()) {
      if (!midnights.includes(t)) push(t, "hour", hourLabel(t));
    }
  } else {
    while (h.getHours() % 6 !== 0) h.setHours(h.getHours() + 1);
    for (let t = h.getTime(); t < window.endMs; h.setHours(h.getHours() + 6), t = h.getTime()) {
      if (!midnights.includes(t)) push(t, "grid", null);
    }
  }
  return ticks;
}

// ---------------------------------------------------------------------------
// Staffing shading (the capacity face)
// ---------------------------------------------------------------------------

// The served calendar names days the way the plant's calendar step does —
// "Mon" … "Sun" — and the runner reads those. Matching only "monday" dropped
// every window, so the unstaffed shade never drew on any board (found
// 2026-09-18 when the bar hatch, built on the same bands, drew nothing).
const DAY_INDEX: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tues: 2, tuesday: 2,
  wed: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

function minutesOfDay(text: string): number | null {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(text.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export interface ShadeBand {
  /** Fractions of the visible window. */
  from: number;
  to: number;
  /** "unstaffed" = no window at all; "reduced" = staffed below the peak crew. */
  kind: "unstaffed" | "reduced";
}

/**
 * Shading bands over a visible window from the calendar's staffed windows:
 * hours no window covers are unstaffed; hours staffed below the calendar's
 * peak concurrent-machine capacity shade lighter. Day names outside the
 * calendar vocabulary are skipped (no shading beats wrong shading).
 */
export function staffingBands(
  windows: BoardCalendarWindow[],
  visible: TimeWindow,
  stepMinutes = 30
): ShadeBand[] {
  const parsed = windows
    .map((w) => ({
      day: DAY_INDEX[w.day.trim().toLowerCase()],
      start: minutesOfDay(w.start),
      end: minutesOfDay(w.end),
      capacity: w.machine_capacity,
    }))
    .filter((w) => w.day !== undefined && w.start != null && w.end != null);
  if (parsed.length === 0) return [];
  const peak = Math.max(...parsed.map((w) => w.capacity));

  const capacityAt = (ms: number): number => {
    const d = new Date(ms);
    const day = d.getDay();
    const minutes = d.getHours() * 60 + d.getMinutes();
    const prevDay = (day + 6) % 7;
    let cap = 0;
    for (const w of parsed) {
      const s = w.start!;
      const e = w.end!;
      if (s <= e) {
        // Same-day window.
        if (w.day === day && minutes >= s && minutes < e) cap = Math.max(cap, w.capacity);
      } else {
        // Overnight window: [s, 24h) on its day, [0, e) on the next.
        if (w.day === day && minutes >= s) cap = Math.max(cap, w.capacity);
        if (w.day === prevDay && minutes < e) cap = Math.max(cap, w.capacity);
      }
    }
    return cap;
  };

  const stepMs = stepMinutes * 60_000;
  const bands: ShadeBand[] = [];
  let current: { kind: ShadeBand["kind"]; fromMs: number } | null = null;
  for (let t = visible.startMs; t < visible.endMs; t += stepMs) {
    const cap = capacityAt(t);
    const kind: ShadeBand["kind"] | null = cap === 0 ? "unstaffed" : cap < peak ? "reduced" : null;
    if (current && current.kind !== kind) {
      bands.push({ from: positionIn(visible, current.fromMs), to: positionIn(visible, t), kind: current.kind });
      current = null;
    }
    if (!current && kind) current = { kind, fromMs: t };
  }
  if (current) {
    bands.push({ from: positionIn(visible, current.fromMs), to: 1, kind: current.kind });
  }
  return bands;
}

/**
 * Owner's option B (2026-09-18): a bar stays whole, but the stretches of it
 * that span UNSTAFFED hours are drawn hatched — the job is paused there, not
 * running — so the shade beneath reads through. Returned as fractions of the
 * BAR (0 = its start, 1 = its end), intersecting the same unstaffed bands the
 * lane shades with. A job a person placed at an exact time is never hatched:
 * they put it there, so it runs there (`hatchExempt`).
 */
export function unstaffedSpans(
  bar: { from: number; to: number },
  bands: ReadonlyArray<ShadeBand>
): Array<{ from: number; to: number }> {
  const width = bar.to - bar.from;
  if (!(width > 0)) return [];
  const out: Array<{ from: number; to: number }> = [];
  for (const b of bands) {
    if (b.kind !== "unstaffed") continue;
    const s = Math.max(bar.from, b.from);
    const e = Math.min(bar.to, b.to);
    if (e > s) out.push({ from: (s - bar.from) / width, to: (e - bar.from) / width });
  }
  return out.sort((a, b) => a.from - b.from);
}
export function hatchExempt(j: Pick<BoardLaneJob, "placement">): boolean {
  return j.placement?.kind === "exact_time";
}

// ---------------------------------------------------------------------------
// Find (Phase B iteration 1 — owner's pick 2a)
// ---------------------------------------------------------------------------

/** One job the query matched, with the machine whose lane holds it. */
export interface FindMatch {
  job: string;
  part: string | null;
  machine: string;
}

/**
 * Jobs whose number OR part contains the query, case-insensitively. Only the
 * Refined track is movable, so a planner who cannot locate a job among 18
 * lanes cannot act on it — this is the locator, never a filter: it returns
 * matches, and the lane stack keeps every machine on screen so the context a
 * move needs stays visible.
 *
 * One entry per JOB (the first lane that holds it wins), in lane order, so
 * the stepper walks machines top-to-bottom the way the board draws them.
 * Callers pass the union of the tracks they want searched — a job Plex holds
 * that the plan does not is still findable that way.
 *
 * A blank or whitespace-only query matches nothing (an empty box is not a
 * search); no query is ever treated as "match everything".
 */
export function findMatches(lanes: BoardLane[], query: string): FindMatch[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const out: FindMatch[] = [];
  const seen = new Set<string>();
  for (const lane of lanes) {
    for (const j of lane.jobs) {
      if (seen.has(j.job)) continue;
      const job = j.job.toLowerCase();
      const part = (j.part ?? "").toLowerCase();
      if (job.includes(needle) || (part !== "" && part.includes(needle))) {
        seen.add(j.job);
        out.push({ job: j.job, part: j.part, machine: lane.machine });
      }
    }
  }
  return out;
}

/**
 * The stepper's position, resolved from the ACTIVE JOB rather than a stored
 * index: a replan reorders lanes and changes what matches, and an index kept
 * across that lands on a different job silently. Returns -1 when the active
 * job is no longer among the matches — the caller re-anchors to the first.
 */
export function findIndexOf(matches: FindMatch[], activeJob: string | null): number {
  if (!activeJob) return matches.length > 0 ? 0 : -1;
  return matches.findIndex((m) => m.job === activeJob);
}

/**
 * Step the stepper, wrapping in both directions. When the active job has
 * fallen out of the matches (a replan moved it, or the query narrowed), the
 * step re-anchors to the first match instead of counting from a position that
 * no longer means anything.
 */
export function stepFind(matches: FindMatch[], activeJob: string | null, delta: number): string | null {
  if (matches.length === 0) return null;
  const at = findIndexOf(matches, activeJob);
  if (at < 0) return matches[0]!.job;
  const next = ((at + delta) % matches.length + matches.length) % matches.length;
  return matches[next]!.job;
}

/** working = the top (editable) row; optimized = the standing plan's track; plex = the schedule import's track. */
/**
 * Iteration 3 item C — the status indicator drawn IN the gap on the Plex line
 * (never a colour on the bar): one state class and its legend wording. The
 * space between two jobs on that line is the time the floor has for the next
 * setup; the hub grades it against the pair's changeover class. `unknown` is
 * drawn neutral and is never green.
 */
export type GapState = NonNullable<BoardLaneJob["plex_setup_sufficiency"]>;
export const GAP_STATES: ReadonlyArray<{ state: GapState; className: string; legend: string }> = [
  { state: "sufficient", className: "boardGapSufficient", legend: "setup fits in the gap" },
  { state: "tight", className: "boardGapTight", legend: "tight — at least ¾ of the setup fits" },
  { state: "insufficient", className: "boardGapInsufficient", legend: "not enough time for the setup" },
  { state: "unknown", className: "boardGapUnknown", legend: "no measured class for this changeover" },
];
export function gapIndicator(j: Pick<BoardLaneJob, "plex_setup_sufficiency">): { state: GapState; className: string } | null {
  const hit = GAP_STATES.find((s) => s.state === j.plex_setup_sufficiency);
  return hit ? { state: hit.state, className: hit.className } : null;
}
/**
 * Where the gap indicator is drawn, in absolute ms, ending at the job's start.
 * A positive gap: the gap itself. A gap of zero or less (Plex booked this job
 * over the one before it — H38, 2026-09-18): the band is the SETUP THE JOB
 * NEEDS, its class hours wide, ending at the start, so the collision is
 * visible on the job's own sub-row; a 4 px marker read as "no changeover".
 * Null when nothing is served, or when the pair has no class to size a band
 * from (the caller draws its marker).
 */
export function gapBandSpan(
  j: Pick<BoardLaneJob, "plex_gap_hours" | "plex_setup_required_hours" | "plex_setup_sufficiency">,
  startMs: number
): { fromMs: number; toMs: number } | null {
  if (j.plex_setup_sufficiency == null || j.plex_gap_hours == null || !Number.isFinite(startMs)) return null;
  if (j.plex_gap_hours > 0) return { fromMs: startMs - j.plex_gap_hours * 3_600_000, toMs: startMs };
  if (j.plex_setup_required_hours != null && j.plex_setup_required_hours > 0) {
    return { fromMs: startMs - j.plex_setup_required_hours * 3_600_000, toMs: startMs };
  }
  return null;
}
const h1 = (n: number) => (Math.abs(n) >= 10 ? Math.round(n).toString() : (Math.round(n * 10) / 10).toString());
/** The hover for the gap: the class, the hours it needs and the gap there is — plain sentences, no jargon. */
/**
 * The Plex line's setup-sufficiency card (item C), as labelled rows.
 *
 * Reads as a verdict with its evidence above it: how much room Plex left, how
 * much the changeover needs, and whether that is enough. The class value is
 * served data and is printed as served; everything we author is a capitalised
 * phrase that ends in a full stop where it is a sentence.
 */
export function gapHoverRows(
  j: Pick<BoardLaneJob, "plex_gap_hours" | "plex_setup_required_hours" | "plex_setup_sufficiency" | "plex_setup_class">
): CardRow[] {
  if (j.plex_setup_sufficiency == null || j.plex_gap_hours == null) return [];
  const gap = j.plex_gap_hours;
  const rows: CardRow[] = [
    {
      label: "Gap",
      value: gap <= 0 ? "None — Plex booked this over the job before it." : `${h1(gap)} h`,
    },
  ];
  if (j.plex_setup_sufficiency === "unknown" || j.plex_setup_required_hours == null) {
    rows.push({ label: "Setup", value: "Not known — this changeover has no measured class." });
    return rows;
  }
  rows.push({ label: "Setup needs", value: `${h1(j.plex_setup_required_hours)} h` });
  if (j.plex_setup_class) rows.push({ label: "Class", value: j.plex_setup_class, muted: true });
  rows.push({
    label: "Verdict",
    value:
      j.plex_setup_sufficiency === "sufficient"
        ? "Enough time for the setup."
        : j.plex_setup_sufficiency === "tight"
          ? "Tight — at least three quarters of the setup fits."
          : "Not enough time for the setup.",
  });
  return rows;
}

export type TrackKind = "working" | "optimized" | "plex";

/**
 * The Plex line is Plex's order laid out from now (owner, 2026-10-08): a job
 * Plex booked earlier than where it is drawn is OVERDUE, and the hover says so
 * beside the booked date. Never on a running job — its drawn start is when it
 * actually began.
 */
export function plexOverdue(j: Pick<BoardLaneJob, "running" | "start" | "plex_scheduled_start">): boolean {
  if (j.running || !j.plex_scheduled_start || !j.start) return false;
  const booked = Date.parse(j.plex_scheduled_start);
  const drawn = Date.parse(j.start);
  return !Number.isNaN(booked) && !Number.isNaN(drawn) && booked < drawn;
}

/**
 * The classes one lane segment wears. Pure, so the encodings can be gated in
 * tests without a DOM: in particular that the Find highlight lands on EVERY
 * track (finding a job is for comparing where the three plans put it, so a
 * highlight on only the working row would defeat the gesture), while the
 * working-row-only encodings — the what-if change outline, the pending mark,
 * the placement mark, clickability — stay on the working row alone.
 */
/**
 * Whether a click on this bar opens the move popover. Iteration 3 item E
 * (owner, 2026-09-18): "you have to be able to move jobs on any row" — the
 * Plex line, Optimized and Refined all open the same popover against the
 * Refined row. The one exception is unchanged: a running job is never movable.
 * `editable` is whether the board is in a refine context at all.
 */
export function movableOnTrack(track: TrackKind, job: Pick<BoardLaneJob, "running">, editable: boolean): boolean {
  void track; // every track — kept in the signature so the rule reads at the call site
  return editable && !job.running;
}

export function jobSegmentClasses(args: {
  atRisk: boolean;
  running: boolean;
  /** what-if: changed vs the standing plan (working row only). */
  changed: boolean;
  clickable: boolean;
  pending: boolean;
  placed: boolean;
  /** the job Find is sitting on. */
  found: boolean;
  track: TrackKind;
}): string {
  const working = args.track === "working";
  return [
    "boardJobSegment",
    args.atRisk ? "boardJobAtRisk" : null,
    args.running ? "boardJobRunning" : null,
    working && args.changed ? "boardJobChanged" : null,
    args.clickable ? "boardJobClickable" : null,
    working && args.pending ? "boardJobPending" : null,
    working && args.placed ? "boardJobPlaced" : null,
    args.found ? "boardJobFound" : null,
  ]
    .filter((c): c is string => c != null)
    .join(" ");
}

/**
 * Piece counts, in thousands-separated figures — never a raw integer string.
 *   quantity only        — not running, or nothing recorded
 *   "310,000 of 750,000" — running, with a figure, plus the share, muted
 * A running job whose produced figure is NULL has recorded nothing, which is
 * NOT the same fact as having made none: it shows the quantity alone. Never
 * "0 of 750,000", never a fabricated zero.
 */
export function pieceLine(job: BoardLaneJob): { text: string; share: string | null } | null {
  const total = job.pieces;
  if (total == null) return null;
  const made = job.pieces_produced;
  if (!job.running || made == null) return { text: total.toLocaleString(), share: null };
  return {
    text: `${made.toLocaleString()} of ${total.toLocaleString()}`,
    share: total > 0 ? `${Math.round((made / total) * 100)}%` : null,
  };
}

/**
 * The CSS max-width of `.boardHoverCard`, in px. The flip decision below uses
 * it rather than a measured width: a card narrower than its maximum flips a
 * little earlier than it strictly had to, which is invisible; a card wider
 * than the number we guessed would be CLIPPED, which is the bug being fixed.
 * Erring toward flipping early is the safe direction. Keep in step with
 * styles.css.
 */
export const HOVER_CARD_MAX_W = 420;

/** A hover card's placement, as CSS offsets from its positioned ancestor. */
export type HoverPlacement = {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
};

/**
 * Where a hover card sits, given the cursor and the lane area's own box.
 *
 * The card used to be pinned at `left: x + 12, top: y + 14` with no regard for
 * the container, so hovering a bar at the right-hand end of the board drew the
 * card off the edge of the screen (owner, 2026-09-19). It now flips to the
 * other side of the cursor when it would overrun, and anchors by the opposite
 * CSS edge when it does — anchoring by `right` lets the browser use the card's
 * REAL width, so nothing here has to guess it.
 *
 * Vertical works the same way against the lane area's height. A card is taller
 * than one line and its height varies with content, so the flip threshold is
 * the container's lower third rather than a measured height; anchoring by
 * `bottom` then grows the card upward from the cursor.
 */
export function hoverCardPosition(
  x: number,
  y: number,
  containerW: number,
  containerH: number,
): HoverPlacement {
  const place: HoverPlacement = {};
  if (containerW > 0 && x + 12 + HOVER_CARD_MAX_W > containerW) {
    place.right = Math.max(0, containerW - x + 12);
  } else {
    place.left = x + 12;
  }
  if (containerH > 0 && y > containerH * (2 / 3)) {
    place.bottom = Math.max(0, containerH - y + 14);
  } else {
    place.top = y + 14;
  }
  return place;
}
